import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../supabase';
import Dropdown from '../../../../components/Dropdown';
import SearchableProjectDropdown from '../../../../components/SearchableProjectDropdown';
import { DropdownItem } from '../../../../components/Dropdown/config';
import { INVOICE_STATUS_OPTIONS, calculateGST, calculateTotal, formatCurrency, shouldMirrorRecordedDate } from '../../config';
import Modal from '../../../../components/Modal';
import './styles.scss';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface AddInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvoiceCreated: () => void;
  teamMembers: TeamMember[];
}

interface FormData {
  invoice_number: string;
  invoice_date: string;
  recorded_date: string;
  project_id: string;
  stage_id: string | null;
  our_fee: string;
  disbursements: string;
  status: string;
  notes: string;
}

interface FormErrors {
  invoice_number?: string;
  invoice_date?: string;
  project_id?: string;
  our_fee?: string;
  general?: string;
}


const AddInvoiceModal: React.FC<AddInvoiceModalProps> = ({
  isOpen,
  onClose,
  onInvoiceCreated,
  teamMembers
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0], // Today's date
    recorded_date: new Date().toISOString().split('T')[0], // Defaults to invoice date
    project_id: '',
    stage_id: null,
    our_fee: '',
    disbursements: '0.00',
    status: 'Draft',
    notes: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [defaultInvoiceNumber, setDefaultInvoiceNumber] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
  const duplicateCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate totals
  const ourFee = parseFloat(formData.our_fee) || 0;
  const disbursements = parseFloat(formData.disbursements) || 0;
  const gstAmount = calculateGST(ourFee, disbursements);
  const totalAmount = calculateTotal(ourFee, disbursements);

  // Fetch the next invoice number (returns just the number, not the TE prefix)
  const fetchNextInvoiceNumber = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .like('invoice_number', 'TE%')
        .order('invoice_number', { ascending: false });

      if (error) throw error;

      // Find the max number from TE-prefixed invoices
      let maxNum = 0;
      if (data && data.length > 0) {
        for (const invoice of data) {
          const match = invoice.invoice_number?.match(/^TE(\d+)$/);
          if (match) {
            const num = parseInt(match[1], 10);
            if (num > maxNum) maxNum = num;
          }
        }
      }

      return `${maxNum + 1}`;
    } catch (err) {
      console.error('Error fetching next invoice number:', err);
      return '1'; // Fallback
    }
  }, []);

  // Check if invoice number already exists (prepends TE prefix for check)
  const checkDuplicateInvoiceNumber = useCallback(async (invoiceNumber: string) => {
    if (!invoiceNumber.trim()) {
      setIsDuplicate(false);
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const fullInvoiceNumber = `TE${invoiceNumber.trim()}`;
      const { data, error } = await supabase
        .from('invoices')
        .select('id')
        .eq('invoice_number', fullInvoiceNumber)
        .limit(1);

      if (error) throw error;

      const exists = data && data.length > 0;
      setIsDuplicate(exists);
      if (exists) {
        setErrors(prev => ({ ...prev, invoice_number: 'Invoice number already exists' }));
      } else {
        setErrors(prev => ({ ...prev, invoice_number: undefined }));
      }
    } catch (err) {
      console.error('Error checking duplicate:', err);
    } finally {
      setIsCheckingDuplicate(false);
    }
  }, []);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset form first
      setFormData({
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        recorded_date: new Date().toISOString().split('T')[0],
        project_id: '',
        stage_id: null,
        our_fee: '',
        disbursements: '0.00',
        status: 'Draft',
        notes: ''
      });
      setErrors({});
      setIsDuplicate(false);
      setIsCheckingDuplicate(false);
      setSelectedClientName(null);

      // Fetch next invoice number
      fetchNextInvoiceNumber().then(nextNumber => {
        setFormData(prev => ({ ...prev, invoice_number: nextNumber }));
        setDefaultInvoiceNumber(nextNumber);
      });
    }

    // Cleanup timeout on close
    return () => {
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
    };
  }, [isOpen, fetchNextInvoiceNumber]);


  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.invoice_number.trim()) {
      newErrors.invoice_number = 'Invoice number is required';
    } else if (isDuplicate) {
      newErrors.invoice_number = 'Invoice number already exists';
    }

    if (!formData.invoice_date) {
      newErrors.invoice_date = 'Invoice date is required';
    }

    if (!formData.project_id) {
      newErrors.project_id = 'Project selection is required';
    }

    if (formData.our_fee && parseFloat(formData.our_fee) < 0) {
      newErrors.our_fee = 'Our fee cannot be negative';
    }

    return newErrors;
  }, [formData, isDuplicate]);

  const createInvoice = useCallback(async (): Promise<number | null> => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return null;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const insertData = {
        invoice_number: `TE${formData.invoice_number.trim()}`,
        invoice_date: formData.invoice_date,
        recorded_date: formData.recorded_date || formData.invoice_date,
        project_id: parseInt(formData.project_id),
        stage_id: formData.stage_id ? parseInt(formData.stage_id) : null,
        our_fee: parseFloat(formData.our_fee) || 0,
        disbursements: parseFloat(formData.disbursements) || 0,
        status: formData.status,
        notes: formData.notes.trim() || null
      };

      const { data: insertedData, error } = await supabase
        .from('invoices')
        .insert(insertData)
        .select('id')
        .single();

      if (error) throw error;

      onInvoiceCreated();
      return insertedData?.id ?? null;
    } catch (err) {
      console.error('Error creating invoice:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'An error occurred while creating the invoice'
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onInvoiceCreated]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const id = await createInvoice();
    if (id !== null) {
      onClose();
    }
  }, [createInvoice, onClose]);

  const handleCreateTaxInvoice = useCallback(async () => {
    const id = await createInvoice();
    if (id !== null) {
      onClose();
      navigate(`/invoices/${id}`);
    }
  }, [createInvoice, onClose, navigate]);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'invoice_date' && shouldMirrorRecordedDate(prev.recorded_date, prev.invoice_date)) {
        next.recorded_date = value;
      }
      return next;
    });
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Debounced duplicate check for invoice number
    if (field === 'invoice_number') {
      setIsDuplicate(false);
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
      duplicateCheckTimeoutRef.current = setTimeout(() => {
        checkDuplicateInvoiceNumber(value);
      }, 500);
    }
  }, [errors, checkDuplicateInvoiceNumber]);

  // Handle project selection
  const handleProjectChange = useCallback((projectId: string | number | null, project?: any) => {
    if (projectId && project) {
      // Check if this is a stage or a project
      if (project.entity_type === 'stage') {
        // For stages: project_id is the parent, stage_id is the stage
        setFormData(prev => ({
          ...prev,
          project_id: project.parent_project_id?.toString() || '',
          stage_id: projectId.toString()
        }));
      } else {
        // For projects: just set project_id, stage_id is null
        setFormData(prev => ({
          ...prev,
          project_id: projectId.toString(),
          stage_id: null
        }));
      }
      // Set client name
      setSelectedClientName(project.client_company_name || null);
      // Clear error
      if (errors.project_id) {
        setErrors(prev => ({ ...prev, project_id: undefined }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        project_id: '',
        stage_id: null
      }));
      setSelectedClientName(null);
    }
  }, [errors]);

  // Create dropdown items
  const statusDropdownItems: DropdownItem[] = INVOICE_STATUS_OPTIONS.map(status => ({
    id: status.id,
    label: status.label,
    value: status.value
  }));

  const modalFooter = (
    <div className="modal-footer-actions">
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <div className="modal-footer-actions__right">
        <button 
          type="submit" 
          form="add-invoice-form"
          disabled={isSubmitting || isCheckingDuplicate || isDuplicate} 
          className="btn-secondary"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
        <button 
          type="button"
          onClick={handleCreateTaxInvoice}
          disabled={isSubmitting || isCheckingDuplicate || isDuplicate} 
          className="btn-primary"
        >
          {isSubmitting ? 'Creating...' : 'Create Tax Invoice'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Invoice"
      size="md"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content">
        <form id="add-invoice-form" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          {/* Row 1: Invoice # (left) and Date (right) - compact */}
          <div className="form-row form-row-header">
            <div className="form-group invoice-number-group">
              <label htmlFor="invoice_number">Invoice # *</label>
              <div className={`invoice-number-input ${!isCheckingDuplicate && !isDuplicate && formData.invoice_number.trim() && formData.invoice_number !== defaultInvoiceNumber ? 'valid' : ''}`}>
                <span className="invoice-prefix">TE</span>
                <input
                  id="invoice_number"
                  type="text"
                  value={formData.invoice_number}
                  onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                  className={errors.invoice_number || isDuplicate ? 'error' : ''}
                />
                {!isCheckingDuplicate && !isDuplicate && formData.invoice_number.trim() && formData.invoice_number !== defaultInvoiceNumber && (
                  <span className="valid-indicator">✓</span>
                )}
              </div>
              {errors.invoice_number && <div className="error-message">{errors.invoice_number}</div>}
            </div>

            <div className="form-group date-group">
              <label htmlFor="invoice_date">Sent *</label>
              <input
                id="invoice_date"
                type="date"
                value={formData.invoice_date}
                onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                className={errors.invoice_date ? 'error' : ''}
              />
              {errors.invoice_date && <div className="error-message">{errors.invoice_date}</div>}
            </div>

            <div className="form-group date-group">
              <label htmlFor="recorded_date" title="Date this invoice is recorded against for budgeting. Defaults to the invoice date.">Recorded</label>
              <input
                id="recorded_date"
                type="date"
                value={formData.recorded_date}
                onChange={(e) => handleInputChange('recorded_date', e.target.value)}
              />
            </div>
          </div>

          {/* Row 2: Project selector (full width) and Status (compact) */}
          <div className="form-row form-row-project">
            <div className="form-group project-group">
              <label htmlFor="project_id">Job *</label>
              <SearchableProjectDropdown
                value={formData.project_id || null}
                onChange={handleProjectChange}
                placeholder="Search and select job..."
                allowNone={false}
                className={errors.project_id ? 'error' : ''}
              />
              {errors.project_id && <div className="error-message">{errors.project_id}</div>}
            </div>

            <div className="form-group client-group">
              <label>Client</label>
              <div className={`client-display ${selectedClientName ? 'has-client' : 'no-client'}`}>
                {selectedClientName || 'Select a job to display client'}
              </div>
            </div>

            <div className="form-group status-group">
              <label htmlFor="status">Status</label>
              <Dropdown
                items={statusDropdownItems}
                value={formData.status}
                onChange={(value) => handleInputChange('status', value)}
                placeholder="Select status"
              />
            </div>
          </div>

          {/* Calculation Summary with Fee Inputs */}
          <div className="calculation-summary">
            <div className="calc-row input-row">
              <label htmlFor="our_fee" className="label">Our Fee *</label>
              <div className="currency-input">
                <span className="currency-symbol">$</span>
                <input
                  id="our_fee"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.our_fee}
                  onChange={(e) => handleInputChange('our_fee', e.target.value)}
                  className={errors.our_fee ? 'error' : ''}
                  placeholder="0.00"
                />
              </div>
            </div>
            {errors.our_fee && <div className="error-message calc-error">{errors.our_fee}</div>}
            
            <div className="calc-row input-row">
              <label htmlFor="disbursements" className="label">Disbursements</label>
              <div className="currency-input">
                <span className="currency-symbol">$</span>
                <input
                  id="disbursements"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.disbursements}
                  onChange={(e) => handleInputChange('disbursements', e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="calc-row subtotal">
              <span className="label">Subtotal:</span>
              <span className="value">{formatCurrency(ourFee + disbursements)}</span>
            </div>
            <div className="calc-row">
              <span className="label">GST (10%):</span>
              <span className="value">{formatCurrency(gstAmount)}</span>
            </div>
            <div className="calc-row total">
              <span className="label">Total Amount:</span>
              <span className="value">{formatCurrency(totalAmount)}</span>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group" id="notes-section">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Optional notes about this invoice..."
              rows={3}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddInvoiceModal;
